import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { Colors } from "../../../Styles/Colors";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { CommonPatientDetails } from "../../../Components/Common Components/CommonComponents";
import { useCounsellorDate } from "../../Ayurveda/Counsellor/CounsellorDateContext";

const DischargeApprovalList = ({ history, fromModule, useSharedDate }) => {
    const sharedDateContext = useCounsellorDate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDate, setFilterDate] = useState(dayjs());
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [approvalChecked, setApprovalChecked] = useState({});
    const [generalComment, setGeneralComment] = useState("");
    const [moduleComments, setModuleComments] = useState({});
    const [saving, setSaving] = useState(false);

    const effectiveDate = useSharedDate && sharedDateContext?.filterDate ? dayjs(sharedDateContext.filterDate) : filterDate;
    const effectiveDateStr = effectiveDate.format("YYYY-MM-DD");

    const fetchList = useCallback(() => {
        setLoading(true);
        const dateStr = effectiveDateStr;
        RestAPIService.getAll(`${Serviceurls.IP_DISCHARGE_APPROVAL}?date=${dateStr}`)
            .then((res) => {
                if (res?.data?.status === "success" && Array.isArray(res.data.data)) {
                    setList(res.data.data);
                } else {
                    setList([]);
                }
            })
            .catch((err) => {
                setErrorMsg(err?.response?.data?.message || "Failed to load list");
                setList([]);
            })
            .finally(() => setLoading(false));
    }, [effectiveDateStr]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const openPopup = (row) => {
        setSelectedRow(row);
        const modules = row?.approval_modules || [];
        const initialChecked = {};
        const initialComments = {};
        modules.forEach((m) => {
            initialChecked[m.id] = !!m.is_approved;
            initialComments[m.id] = "";
        });
        setApprovalChecked(initialChecked);
        setModuleComments(initialComments);
        setGeneralComment("");
        setPopupOpen(true);
    };

    const closePopup = () => {
        setPopupOpen(false);
        setSelectedRow(null);
    };

    const handleModuleCheck = (moduleId, currentModule) => {
        if (!currentModule) return;
        setApprovalChecked((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    const handleSave = () => {
        if (!selectedRow) return;
        const modules = selectedRow.approval_modules || [];
        const currentModule = modules.find((m) => m.current_module === true);
        if (!currentModule) {
            setErrorMsg("No module is pending your approval.");
            return;
        }
        const isChecked = approvalChecked[currentModule.id];
        if (!isChecked) {
            setErrorMsg("Please check your department to approve.");
            return;
        }
        setSaving(true);
        setErrorMsg("");
        RestAPIService.create(
            {
                ip_admission_id: selectedRow.id,
                id: currentModule.id,
                comments: generalComment,
            },
            Serviceurls.IP_DISCHARGE_APPROVAL
        )
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsg(res?.data?.message || "Saved successfully");
                    closePopup();
                    fetchList();
                } else {
                    setErrorMsg(res?.data?.message || "Save failed");
                }
            })
            .catch((err) => {
                setErrorMsg(err?.response?.data?.message || "Save failed");
            })
            .finally(() => setSaving(false));
    };

    const columns = [
        {
            field: "patientDetails",
            headerName: "Patient Details",
            flex: 0.22,
            minWidth: 200,
            renderCell: (params) => (
                <Box>
                    <CommonPatientDetails data={params.row} hideInsurance={true} />
                </Box>
            ),
        },
        {
            field: "ip_number",
            headerName: "IP Number",
            flex: 0.12,
            minWidth: 120,
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.85rem" }}>{params.row?.ip_number || "-"}</Typography>
            ),
        },
        { field: "ward_name", headerName: "Ward", flex: 0.08, minWidth: 80 },
        { field: "room_number", headerName: "Room", flex: 0.08, minWidth: 80 },
        { field: "bed_number", headerName: "Bed", flex: 0.06, minWidth: 60 },
        { field: "admission_date", headerName: "Admission Date", flex: 0.14, minWidth: 140 },
        { field: "discharge_date", headerName: "Discharge Date", flex: 0.1, minWidth: 100 },
        {
            field: "action",
            headerName: "Action",
            flex: 0.08,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => {
                const modules = params.row?.approval_modules || [];
                const currentModule = modules.find((m) => m.current_module === true);
                const isApprovedByCurrentModule = currentModule && currentModule.is_approved;
                if (isApprovedByCurrentModule) {
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", color: "success.main" }}>
                            <CheckCircleIcon sx={{ fontSize: "1.5rem" }} />
                        </Box>
                    );
                }
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openPopup(params.row)}
                        sx={{ textTransform: "none" }}
                    >
                        Approve
                    </Button>
                );
            },
        },
    ];

    const modules = selectedRow?.approval_modules || [];

    return (
        <Box sx={{ p: 1.5, bgcolor: Colors.Background, minHeight: "60vh" }}>
            <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", gap: 2, mb: 1.5 }}>
                {!useSharedDate && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DatePicker
                            label="Date"
                            value={filterDate}
                            onChange={(d) => d && setFilterDate(dayjs(d))}
                            format="DD-MM-YYYY"
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                    </LocalizationProvider>
                )}
            </Box>

            <Box sx={{ bgcolor: "white", height: '50dvh', borderRadius: 1, overflow: "hidden" }}>
                {loading ? (
                    <Loader />
                ) : (
                    <DataGrid
                        rows={list.map((r, i) => ({ ...r, _id: r.id + "-" + i }))}
                        getRowId={(row) => row._id}
                        columns={columns}
                        autoHeight
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        disableRowSelectionOnClick
                        sx={{
                            "& .MuiDataGrid-columnHeaders": { backgroundColor: Colors.TableHeaderBarBGColor || "#f5f5f5" },
                        }}
                    />
                )}
            </Box>

            <Dialog open={popupOpen} onClose={closePopup} maxWidth="sm" fullWidth>
                <DialogTitle>Discharge Approval</DialogTitle>
                <DialogContent>
                    {selectedRow && (
                        <Box sx={{ pt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {selectedRow.patient_name} | {selectedRow.ip_number}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, mb: 1 }}>Departments</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Departments</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {modules.map((mod) => (
                                            <TableRow key={mod.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={!!approvalChecked[mod.id]}
                                                        disabled={!mod.current_module}
                                                        onChange={() => handleModuleCheck(mod.id, mod.current_module)}
                                                        size="small"
                                                    />
                                                    {mod.display_name}
                                                    {mod.is_approved && (
                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                            (Approved)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {typeof mod?.comments === "string"
                                                                ? mod?.comments
                                                                : mod?.comments && typeof mod.comments === "object" && mod.comments !== null
                                                                ? (mod?.comments[mod.id] ?? "-")
                                                                : "-"}
                                                        </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Comments</Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={generalComment}
                                onChange={(e) => setGeneralComment(e.target.value)}
                                placeholder="General comments..."
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closePopup}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: "#04B7B1", "&:hover": { bgcolor: "#039E99" } }}>
                        SAVE
                    </Button>
                </DialogActions>
            </Dialog>

            {errorMsg && <ToastMsg severity="error" msg={errorMsg} msgPop={() => setErrorMsg("")} />}
            {successMsg && <ToastMsg severity="success" msg={successMsg} msgPop={() => setSuccessMsg("")} />}
        </Box>
    );
};

export default DischargeApprovalList;
